// Third-party
import React from 'react';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

// Components
import DropdownMenu from './DropdownMenu';
import SearchInput from './SearchInput';
import { Tooltip } from '../tooltips';

export type AutoCompleteResult = {
  _id: string;
  text: string;
};

type Props = {
  className?: string;
  dark?: boolean;
  compact?: boolean;
  results: AutoCompleteResult[];
  placeholder?: string;
  noResultsText?: string;
  onSearch: (query: string) => Promise<void>;
  onSelect: (item: AutoCompleteResult) => void;
  debounceDelay?: number;
};

type State = {
  query: string;
  loading: boolean;
};

class AutoCompleteInput extends React.Component<Props, State> {
  private dropdownMenuRef: DropdownMenu | null = null;

  private searchInputRef: SearchInput | null = null;

  debouncedSearch = debounce(() => {
    const { query } = this.state;
    const { onSearch } = this.props;

    onSearch(query).then(() => {
      this.setState({ loading: false });
    });
  }, 300);

  constructor(props: Props) {
    super(props);

    this.state = {
      query: '',
      loading: false,
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  renderResults = () => {
    const { results } = this.props;

    return results.map(result => (
      <div key={result._id} data-id={result._id} className="auto-complete-item d-flex">
        <div className="auto-complete-item-text flex-fill">
          <Tooltip message={result.text} delayShow={300} data-private data-dd-privacy="mask">
            <span>{result.text}</span>
          </Tooltip>
        </div>
      </div>
    ));
  };

  onItemSelect = (value: any) => {
    if (value == null) {
      return;
    }

    const result = this.props.results.find(r => r._id === value.key);

    if (!result) {
      return;
    }

    this.props.onSelect(result);
  };

  onSearch = (query: string) => {
    this.setState({ query, loading: !!query });

    this.debouncedSearch();
  };

  handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (
      this.searchInputRef &&
      this.searchInputRef.input.current &&
      this.searchInputRef.input.current.inputContainer.current &&
      this.searchInputRef.input.current.inputContainer.current.contains(event.target)
    ) {
      return;
    }

    const menuRef = this.dropdownMenuRef && this.dropdownMenuRef.getMenuRef();

    if (menuRef && menuRef.contains(event.target)) {
      return;
    }

    // Clear search query and results by searching with an empty string.
    this.onSearch('');
  };

  render() {
    const { results, className, dark, placeholder, noResultsText, compact } = this.props;
    const { query, loading } = this.state;

    const isExtended = !loading && !!query && (results.length > 0 || !!noResultsText);

    const emptyState = <span>{noResultsText}</span>;

    return (
      <DropdownMenu
        ref={ref => {
          this.dropdownMenuRef = ref;
        }}
        isExtended={isExtended}
        options={this.renderResults()}
        className={classNames('auto-complete-input', className)}
        dropdownClass="auto-complete-results"
        onSelect={this.onItemSelect}
        emptyState={emptyState}
      >
        <SearchInput
          ref={ref => {
            this.searchInputRef = ref;
          }}
          dark={dark}
          compact={compact}
          onChange={this.onSearch}
          placeholder={placeholder}
          loading={loading}
        />
      </DropdownMenu>
    );
  }
}

export default AutoCompleteInput;
